/**
 * MatchPanel — redesigned with GroupBracket, connectors, EntityRow.
 * Preserves all cascade logic from original, adds visual hierarchy.
 */
import React, { useMemo, useCallback, useEffect, useState } from "react";
import type { TicketMatch, EntityTypeConfig, EntityMatch, EntitySearchResult, CorrectionItem } from "../api/types";
import { EntityRow } from "./EntityRow";
import { CorrectionSearch } from "./CorrectionSearch";
import { CascadeDialog } from "./CascadeDialog";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { fetchEntityByPk } from "../api/client";
import { stripPkPrefix } from "../helpers/entityUtils";

const EMPTY_MATCH: EntityMatch = { best: null, alternatives: [], needs_review: false };
const AUTO_CONFIRM = new Set(["exact", "EXACT", "learned", "LEARNED"]);
function shouldAutoConfirm(m: EntityMatch): boolean {
  if (!m.best || !m.best.entity) return false;
  return m.best.score >= 0.9 && AUTO_CONFIRM.has(m.best.method);
}

export interface MatchPanelProps {
  ticket: TicketMatch;
  entityTypes: EntityTypeConfig[];
  keyEntities?: string[];
  extractedEntities?: Record<string, Record<string, unknown> | null>;
  verdicts: Record<string, "ok" | "corrected">;
  onVerdict: (entityKey: string, verdict: "ok" | "corrected" | undefined) => void;
  onCorrection: (correction: CorrectionItem) => void;
  onCascadeReset?: (entityKeys: string[]) => void;
  /** Full reset: clear all corrections + verdicts in parent */
  onResetAll?: () => void;
  readOnly?: boolean;
}

function orderEntities(ets: EntityTypeConfig[]) {
  const derivedKeys = new Set<string>();
  for (const et of ets) if (et.derived_entities) for (const d of et.derived_entities) derivedKeys.add(d.entity_type);
  const roots: EntityTypeConfig[] = [], children: EntityTypeConfig[] = [], free: EntityTypeConfig[] = [];
  for (const et of ets) {
    if (derivedKeys.has(et.key)) continue;
    if (!et.parent_entity && !et.has_parent) roots.push(et);
    else if (et.parent_entity) children.push(et);
    else free.push(et);
  }
  return { ordered: [...roots, ...children, ...free], derivedKeys };
}

/* ── Visual sub-components ── */

function GroupBracket({ title, icon, subtitle, allConfirmed, children }: {
  title: string; icon: string; subtitle: string; allConfirmed: boolean; children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ marginBottom: "var(--space-3)" }}>
      <div className="card-header" style={{ gap: 8, background: allConfirmed ? "var(--indaco-success-light)" : "var(--indaco-gray-50)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-700)" }}>{icon}</span>
        <span className="text-sm fw-semibold">{title}</span>
        <span className="text-xs" style={{ color: "var(--indaco-gray-600)" }}>{subtitle}</span>
        <span style={{ flex: 1 }} />
        {allConfirmed && (
          <span className="badge badge-success">
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check</span> Validato
          </span>
        )}
      </div>
      <div style={{ padding: "var(--space-3)" }}>{children}</div>
    </div>
  );
}

function Connector({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0 3px 24px", position: "relative" }}>
      <div style={{ position: "absolute", left: 30, top: -4, bottom: -4, width: 2, background: "var(--indaco-gray-300)", borderRadius: 2 }} />
      <div style={{ marginLeft: 26, fontSize: 10, fontWeight: 600, color: "var(--indaco-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    </div>
  );
}

/* ── MatchPanel ── */

export function MatchPanel({ ticket, entityTypes, extractedEntities, verdicts, onVerdict, onCorrection, onCascadeReset, onResetAll, readOnly }: MatchPanelProps) {
  const { ordered, derivedKeys } = useMemo(() => orderEntities(entityTypes), [entityTypes]);
  const parentKeys = useMemo(() => { const s = new Set<string>(); for (const et of entityTypes) if (et.parent_entity) s.add(et.parent_entity); return s; }, [entityTypes]);
  const [overrides, setOverrides] = useState<Record<string, EntityMatch>>({});
  const [searchOpenFor, setSearchOpenFor] = useState<string | null>(null);
  const [localCorrections, setLocalCorrections] = useState<CorrectionItem[]>([]);
  const [pendingFor, setPendingFor] = useState<string | null>(null);
  const [pendingEntity, setPendingEntity] = useState<EntitySearchResult | null>(null);
  const [resetKeysSet, setResetKeysSet] = useState<Set<string>>(new Set());

  // Dialog for confirming NEW entities (not in DB)
  const [newEntityConfirm, setNewEntityConfirm] = useState<{ entityKey: string; label: string } | null>(null);

  const [cascade, setCascade] = useState<{
    type: "UP" | "DOWN"; correction: CorrectionItem; changedKey: string;
    changedEntity: { pk: string; fields: Record<string, unknown> };
    fromEdificio?: { pk: string; label: string }; toEdificio?: { pk: string; label: string };
    newImpianto?: { pk: string; label: string }; newAmministratore?: { pk: string; label: string };
    currentImpianto?: { pk: string; label: string };
  } | null>(null);

  // Auto-confirm on mount
  useEffect(() => {
    if (readOnly) return;
    for (const et of ordered) {
      if (derivedKeys.has(et.key)) continue;
      const m = overrides[et.key] ?? ticket.entity_matches[et.key];
      if (m && shouldAutoConfirm(m) && !verdicts[et.key]) onVerdict(et.key, "ok");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, readOnly]);

  const getMatch = useCallback((key: string): EntityMatch => overrides[key] ?? ticket.entity_matches[key] ?? EMPTY_MATCH, [overrides, ticket]);

  const resolveCascade = useCallback(async (changedKey: string, changedEntity: { pk: string; fields: Record<string, unknown> }) => {
    const cEt = entityTypes.find((e) => e.key === changedKey);
    if (!cEt) return;
    const newOv: Record<string, EntityMatch> = {};
    const newReset: string[] = [];
    if (cEt.parent_entity && cEt.parent_fk_field) {
      const ppk = String(changedEntity.fields[cEt.parent_fk_field] ?? "");
      if (ppk) try {
        const pe = await fetchEntityByPk(cEt.parent_entity, ppk);
        newOv[cEt.parent_entity] = { best: { entity: { pk: pe.pk, fields: pe.fields }, display_label: pe.label || pe.pk, score: 1.0, method: "cascade" }, alternatives: [], needs_review: false };
        const pEt = entityTypes.find((e) => e.key === cEt.parent_entity);
        if (pEt?.derived_entities) for (const d of pEt.derived_entities) {
          const fk = String(pe.fields[d.fk_field] ?? "");
          if (fk) try { const de = await fetchEntityByPk(d.entity_type, fk); newOv[d.entity_type] = { best: { entity: { pk: de.pk, fields: de.fields }, display_label: de.label || de.pk, score: 1.0, method: "fk_lookup" }, alternatives: [], needs_review: false }; } catch {/* */}
        }
      } catch {/* */}
    }
    if (cEt.derived_entities) {
      for (const d of cEt.derived_entities) {
        const fk = String(changedEntity.fields[d.fk_field] ?? "");
        if (fk) try { const de = await fetchEntityByPk(d.entity_type, fk); newOv[d.entity_type] = { best: { entity: { pk: de.pk, fields: de.fields }, display_label: de.label || de.pk, score: 1.0, method: "fk_lookup" }, alternatives: [], needs_review: false }; } catch {/* */}
      }
      for (const et of entityTypes) if (et.key !== changedKey && et.parent_entity === changedKey) newReset.push(et.key);
    }
    if (Object.keys(newOv).length) {
      setOverrides((p) => ({ ...p, ...newOv }));
      for (const [k, m] of Object.entries(newOv)) if (m.best?.entity) onVerdict(k, "ok");
    }
    if (newReset.length) {
      setResetKeysSet((p) => { const n = new Set(p); newReset.forEach((k) => n.add(k)); return n; });
      onCascadeReset?.(newReset);
    }
  }, [entityTypes, onVerdict, onCascadeReset]);

  function handlePick(entityKey: string, picked: EntitySearchResult) {
    setPendingFor(entityKey);
    setPendingEntity(picked);
    setSearchOpenFor(null);
  }

  function confirmWithFeedback(entityKey: string, entity: EntitySearchResult, categories: string[], note: string) {
    const best = getMatch(entityKey).best;
    const correction: CorrectionItem = {
      ticket_index: ticket.ticket_index, field_corrected: entityKey,
      original_value: best?.entity ? { pk: best.entity.pk, ...best.entity.fields } : null,
      corrected_value: { pk: entity.pk, ...entity.fields, display_label: entity.label },
      correction_category: categories[0] || "match_errato", correction_categories: categories, correction_note: note,
    };
    const et = entityTypes.find((e) => e.key === entityKey);

    // Check UP cascade
    if (et?.parent_entity && et.parent_fk_field) {
      const npk = String(entity.fields[et.parent_fk_field] ?? "");
      const cpk = getMatch(et.parent_entity).best?.entity?.pk ?? "";
      if (npk && cpk && npk !== cpk) {
        setCascade({ type: "UP", correction, changedKey: entityKey, changedEntity: { pk: entity.pk, fields: entity.fields },
          fromEdificio: { pk: cpk, label: getMatch(et.parent_entity).best?.display_label ?? cpk },
          toEdificio: { pk: npk, label: npk }, newImpianto: { pk: entity.pk, label: entity.label } });
        setPendingFor(null); setPendingEntity(null);
        return;
      }
    }
    // Check DOWN cascade
    if (et?.derived_entities || entityTypes.some((c) => c.parent_entity === entityKey)) {
      const childEt = entityTypes.find((c) => c.parent_entity === entityKey);
      const childM = childEt ? getMatch(childEt.key) : null;
      if (childM?.best?.entity && childEt?.parent_fk_field) {
        const childPpk = String(childM.best.entity.fields[childEt.parent_fk_field] ?? "");
        if (childPpk && childPpk !== entity.pk) {
          setCascade({ type: "DOWN", correction, changedKey: entityKey, changedEntity: { pk: entity.pk, fields: entity.fields },
            fromEdificio: { pk: getMatch(entityKey).best?.entity?.pk ?? "", label: getMatch(entityKey).best?.display_label ?? "" },
            toEdificio: { pk: entity.pk, label: entity.label },
            currentImpianto: { pk: childM.best.entity.pk, label: childM.best.display_label } });
          setPendingFor(null); setPendingEntity(null);
          return;
        }
      }
    }
    applyCorrection(correction, entityKey, { pk: entity.pk, fields: entity.fields });
  }

  function applyCorrection(correction: CorrectionItem, key: string, entity: { pk: string; fields: Record<string, unknown> }) {
    onCorrection(correction);
    setLocalCorrections((p) => [...p.filter((c) => c.field_corrected !== key), correction]);
    onVerdict(key, "corrected");
    setResetKeysSet((p) => { const n = new Set(p); n.delete(key); return n; });
    resolveCascade(key, entity);
    setPendingFor(null); setPendingEntity(null);
  }

  function handleCascadeConfirm() {
    if (!cascade) return;
    applyCorrection(cascade.correction, cascade.changedKey, cascade.changedEntity);
    setCascade(null);
  }

  /** DOWN cascade: operator picked an impianto from the new edificio's list */
  function handleCascadeWithImpianto(pickedImpianto: EntitySearchResult | null) {
    if (!cascade) return;
    // Apply the edificio correction
    applyCorrection(cascade.correction, cascade.changedKey, cascade.changedEntity);
    // If an impianto was picked, set it as override (no reset needed)
    if (pickedImpianto) {
      const impEt = entityTypes.find((e) => e.parent_entity === cascade.changedKey);
      if (impEt) {
        setOverrides((p) => ({
          ...p,
          [impEt.key]: {
            best: { entity: { pk: pickedImpianto.pk, fields: pickedImpianto.fields }, display_label: pickedImpianto.label, score: 1.0, method: "manual" },
            alternatives: [], needs_review: false,
          },
        }));
        setResetKeysSet((p) => { const n = new Set(p); n.delete(impEt.key); return n; });
        onVerdict(impEt.key, "ok");
      }
    }
    setCascade(null);
  }

  /** Full reset: clear everything locally + tell parent to wipe corrections/verdicts */
  function fullReset() {
    setLocalCorrections([]);
    setOverrides({});
    setResetKeysSet(new Set());
    setSearchOpenFor(null);
    setPendingFor(null);
    setPendingEntity(null);
    onResetAll?.();
  }

  function hint(key: string): string | null {
    if (!extractedEntities?.[key]) return null;
    const vals = Object.values(extractedEntities[key]!).filter((v) => v && String(v).trim()).map(String);
    return vals.length ? vals.join(", ").slice(0, 60) : null;
  }

  const linkedRoot = ordered.find((et) => !et.parent_entity && !et.has_parent && et.derived_entities?.length);
  const linkedChildren = ordered.filter((et) => et.parent_entity === linkedRoot?.key);
  const linkedDerived = (linkedRoot?.derived_entities?.map((d) => entityTypes.find((e) => e.key === d.entity_type)).filter(Boolean) ?? []) as EntityTypeConfig[];
  const independentEntities = ordered.filter((et) => et !== linkedRoot && !linkedChildren.includes(et));

  const isValid = (key: string) => verdicts[key] === "ok" || verdicts[key] === "corrected" || localCorrections.some((c) => c.field_corrected === key);
  const linkedKeys = [linkedRoot?.key, ...linkedChildren.map((c) => c.key)].filter(Boolean) as string[];
  const blockValid = linkedKeys.every(isValid);

  function doConfirm(entityKey: string) {
    onVerdict(entityKey, "ok");
    const et = entityTypes.find((e) => e.key === entityKey);
    if (et?.parent_entity && getMatch(et.parent_entity).best?.entity) {
      onVerdict(et.parent_entity, "ok");
    }
  }

  function handleConfirm(entityKey: string) {
    const match = getMatch(entityKey);
    if (match.best?.entity?.pk === "NEW") {
      const label = match.best.display_label || "Questo contatto";
      setNewEntityConfirm({ entityKey, label });
      return;
    }
    doConfirm(entityKey);
  }

  function renderRow(et: EntityTypeConfig, opts: { isDerived?: boolean; isParent?: boolean } = {}) {
    const match = getMatch(et.key);
    const corrTo = localCorrections.find((c) => c.field_corrected === et.key)?.corrected_value as Record<string, unknown> | null;
    const corrEntity = corrTo ? { pk: String(corrTo.pk), label: String(corrTo.display_label ?? corrTo.descrizione ?? corrTo.pk), fields: corrTo, score: 1 } : null;
    const parentMatch = et.parent_entity ? getMatch(et.parent_entity) : null;
    const scopePk = parentMatch?.best?.entity?.pk;

    return (
      <div key={et.key} style={{ position: "relative" }}>
        <EntityRow entityType={et} match={match} ticketIndex={ticket.ticket_index}
          verdict={corrEntity ? "corrected" : verdicts[et.key]}
          correctedTo={corrEntity} isDerived={opts.isDerived} isParent={opts.isParent}
          isReset={resetKeysSet.has(et.key) && !overrides[et.key] && !corrEntity}
          readOnly={readOnly}
          onConfirm={() => handleConfirm(et.key)}
          onOpenSearch={() => setSearchOpenFor(et.key)}
          onUndo={fullReset}
          onPickAlt={(alt) => handlePick(et.key, { pk: alt.entity.pk, label: alt.display_label, fields: alt.entity.fields, score: alt.score })}
          extractedHint={hint(et.key)}
          pendingEntity={pendingFor === et.key ? pendingEntity : null}
          onConfirmWithFeedback={pendingFor === et.key ? (ent, cats, note) => confirmWithFeedback(et.key, ent, cats, note) : undefined}
          onCancelPending={pendingFor === et.key ? () => { setPendingFor(null); setPendingEntity(null); } : undefined}
        />
        {searchOpenFor === et.key && (
          <CorrectionSearch entityType={et} currentPk={match.best?.entity?.pk} currentEdificioPk={scopePk}
            onPick={(r) => handlePick(et.key, r)} onClose={() => setSearchOpenFor(null)} />
        )}
      </div>
    );
  }

  return (
    <div onClick={() => setSearchOpenFor(null)}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
        <span className="fw-semibold">Risultato matching</span>
        <ConfidenceBadge score={ticket.overall_confidence} />
      </div>

      {linkedRoot && (
        <div onClick={(e) => e.stopPropagation()}>
          <GroupBracket title="Blocco edificio" icon="hub" subtitle="edificio → impianto → amministratore (collegati)" allConfirmed={blockValid}>
            {renderRow(linkedRoot, { isParent: true })}
            <Connector label="stesso edificio" />
            {linkedChildren.map((et) => <React.Fragment key={et.key}>{renderRow(et)}</React.Fragment>)}
            {linkedDerived.length > 0 && <Connector label="deriva da edificio" />}
            {linkedDerived.map((et) => <React.Fragment key={et.key}>{renderRow(et, { isDerived: true })}</React.Fragment>)}
          </GroupBracket>
        </div>
      )}

      {independentEntities.map((et) => (
        <div key={et.key} onClick={(e) => e.stopPropagation()}>
          <GroupBracket title="Contatto" icon="contacts" subtitle={`${et.label.toLowerCase()} (entità indipendente)`} allConfirmed={isValid(et.key)}>
            {renderRow(et)}
          </GroupBracket>
        </div>
      ))}

      {/* Changes summary: shows ALL diffs (explicit + cascade + derived) */}
      {(() => {
        const diffs: { label: string; oldLabel: string; newLabel: string }[] = [];
        // All entity keys in the linked block + independents
        const allKeys = [...(linkedRoot ? [linkedRoot, ...linkedChildren, ...linkedDerived] : []), ...independentEntities];
        for (const et of allKeys) {
          const orig = ticket.entity_matches[et.key]?.best;
          const curr = getMatch(et.key).best;
          const origPk = orig?.entity?.pk ?? "";
          const currPk = curr?.entity?.pk ?? "";
          if (currPk && origPk !== currPk) {
            diffs.push({
              label: et.label,
                      oldLabel: orig ? `${orig.display_label} (${stripPkPrefix(origPk)})` : "—",
                      newLabel: curr ? `${curr.display_label} (${stripPkPrefix(currPk)})` : "—",
            });
          }
        }
        if (!diffs.length) return null;
        return (
          <div style={{ marginTop: "var(--space-3)", background: "var(--indaco-info-light)", borderLeft: "4px solid var(--indaco-info)", borderRadius: "var(--border-radius-sm)", padding: "var(--space-3)" }}>
            <div className="text-sm fw-semibold" style={{ marginBottom: "var(--space-2)", display: "flex", alignItems: "center", gap: 4 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
              {diffs.length} {diffs.length === 1 ? "modifica" : "modifiche"}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {diffs.map((d) => (
                  <tr key={d.label} style={{ borderBottom: "1px solid var(--indaco-gray-200)" }}>
                    <td style={{ padding: "6px 8px 6px 0", fontWeight: 600, color: "var(--indaco-brand-primary)", whiteSpace: "nowrap", width: 1 }}>{d.label}</td>
                    <td style={{ padding: "6px 4px", opacity: 0.5, textDecoration: "line-through" }}>{d.oldLabel}</td>
                    <td style={{ padding: "6px 4px", width: 20, textAlign: "center" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-gray-400)" }}>arrow_forward</span>
                    </td>
                    <td style={{ padding: "6px 4px", fontWeight: 600 }}>{d.newLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      <CascadeDialog open={!!cascade} type={cascade?.type ?? "UP"}
        fromEdificio={cascade?.fromEdificio} toEdificio={cascade?.toEdificio}
        newImpianto={cascade?.newImpianto} newAmministratore={cascade?.newAmministratore}
        currentImpianto={cascade?.currentImpianto}
        onConfirm={handleCascadeConfirm} onCancel={() => setCascade(null)}
        onConfirmWithImpianto={handleCascadeWithImpianto} />

      {/* Dialog: confirm NEW entity (not in DB) */}
      {newEntityConfirm && (
        <div className="dialog-overlay" onClick={() => setNewEntityConfirm(null)}>
          <div className="dialog-card" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "var(--space-4)" }}>
              <span style={{
                width: 36, height: 36, borderRadius: "var(--border-radius-lg)",
                background: "var(--indaco-new-entity-light)", color: "var(--indaco-new-entity)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>person_add</span>
              </span>
              <div>
                <div className="text-sm fw-semibold">{newEntityConfirm.label}</div>
                <div className="text-xs" style={{ color: "var(--indaco-gray-600)" }}>Non presente in anagrafica</div>
              </div>
            </div>
            <p className="text-sm" style={{ color: "var(--indaco-gray-700)", margin: "0 0 20px", lineHeight: 1.5 }}>
              Questo contatto non è stato trovato nel database. Vuoi confermarlo così com'è o aggiungerlo in anagrafica per i prossimi match?
            </p>
            <div className="dialog-footer">
              <button className="btn btn-outline" onClick={() => { doConfirm(newEntityConfirm.entityKey); setNewEntityConfirm(null); }}>
                Conferma senza aggiungere
              </button>
              <button className="btn btn-success" onClick={() => {
                doConfirm(newEntityConfirm.entityKey);
                setNewEntityConfirm(null);
                // TODO: call API to add entity to DB when backend is ready
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span>
                Aggiungi in anagrafica
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Returns entity keys that MUST be confirmed before sending to queue.
 * Only key entities (from confidence config) are mandatory.
 * Other entities (e.g. chiamante) are optional — nice to confirm but won't block.
 */
export function getConfirmableKeys(entityTypes: EntityTypeConfig[], keyEntities?: string[]): string[] {
  const { ordered, derivedKeys } = orderEntities(entityTypes);
  // Parent keys: entities that have children pointing to them
  const parentKeys = new Set<string>();
  for (const et of entityTypes) if (et.parent_entity) parentKeys.add(et.parent_entity);
  const confirmable = ordered.filter((et) => !derivedKeys.has(et.key) && !parentKeys.has(et.key)).map((et) => et.key);
  // If keyEntities provided, only those are mandatory
  if (keyEntities && keyEntities.length > 0) {
    const keySet = new Set(keyEntities);
    return confirmable.filter((k) => keySet.has(k));
  }
  return confirmable;
}